import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Confetti from 'react-confetti';

import { celebrationsWithDaysLeft } from '../utils/celebrationWindow';

import './CelebrationDialog.scss';

const AVATAR_COLORS = ['#22c55e', '#6366f1', '#8b5cf6', '#ec4899', '#0ea5e9', '#d97706', '#64748b'];

const initialsFrom = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || '?';
};

const colorForName = (name) => {
  let h = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i += 1) {
    h = (h + s.charCodeAt(i) * (i + 3)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[h];
};

const CelebrationAvatar = ({ name, profilePicture }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const src = String(profilePicture || '').trim();
  const showImg = Boolean(src) && !imgFailed;
  const initials = initialsFrom(name);

  useEffect(() => {
    setImgFailed(false);
  }, [src]);

  if (showImg) {
    return (
      <div
        className="rounded-circle overflow-hidden mb-3 border border-primary"
        style={{ width: 100, height: 100 }}
      >
        <img
          src={src}
          alt={name || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }

  return (
    <div
      className="rounded-circle mb-3 d-flex align-items-center justify-content-center border border-primary"
      style={{
        width: 100,
        height: 100,
        background: colorForName(name),
        color: '#fff',
        fontSize: 32,
        fontWeight: 700,
        letterSpacing: '0.02em'
      }}
      aria-label={name || 'Avatar'}
    >
      {initials}
    </div>
  );
};

const CelebrationModal = ({
  show,
  onClose,
  todaysDOBs = [],
  todaysWorkAnniversary = [],
  title = '🎉 Celebration 🎉'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [todayCelebrations, setTodayCelebrations] = useState([]);
  const [shouldShow, setShouldShow] = useState(false);

  // Close handler for button/backdrop
  const handleClose = () => {
    setShouldShow(false);
    onClose?.();
  };

  useEffect(() => {
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;

    // Check if modal has already shown today
    const lastShown = localStorage.getItem('celebrationShownDate');

    if (show && lastShown !== todayKey) {
      // Same daysLeft === 0 rule as the Birthdays / Anniversary widgets
      const birthdaysToday = celebrationsWithDaysLeft(todaysDOBs, 'dateOfBirth')
        .filter(({ daysLeft }) => daysLeft === 0)
        .map(({ item }) => ({ ...item, type: 'birthday' }));

      const anniversariesToday = celebrationsWithDaysLeft(todaysWorkAnniversary, 'dateOfJoining')
        .filter(({ daysLeft }) => daysLeft === 0)
        .map(({ item }) => ({ ...item, type: 'anniversary' }));

      const merged = [...birthdaysToday, ...anniversariesToday];

      if (merged.length > 0) {
        setTodayCelebrations(merged);
        setCurrentIndex(0);
        setShouldShow(true);

        // Mark as shown for today
        localStorage.setItem('celebrationShownDate', todayKey);
      }
    }
  }, [show, todaysDOBs, todaysWorkAnniversary]);

  useEffect(() => {
    if (!shouldShow) return;

    const count = todayCelebrations.length;
    if (count === 0) {
      const timer = setTimeout(handleClose, 6000);
      return () => clearTimeout(timer);
    }

    // One person: auto-close. Several: auto-advance until the last slide, then wait for Close.
    if (count === 1 || currentIndex < count - 1) {
      const timer = setTimeout(() => {
        if (count === 1) {
          handleClose();
        } else {
          setCurrentIndex((i) => i + 1);
        }
      }, 6000);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [shouldShow, currentIndex, todayCelebrations]);

  useEffect(() => {
    if (!shouldShow || todayCelebrations.length <= 1) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setCurrentIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex((i) => Math.min(todayCelebrations.length - 1, i + 1));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shouldShow, todayCelebrations.length]);

  if (!shouldShow || todayCelebrations.length === 0) return null;

  const modalRoot = document.getElementById('modal-root') || document.body;
  const person = todayCelebrations[currentIndex];
  const hasMultiple = todayCelebrations.length > 1;
  const atStart = currentIndex <= 0;
  const atEnd = currentIndex >= todayCelebrations.length - 1;

  const goPrev = (e) => {
    e?.stopPropagation();
    setCurrentIndex((i) => Math.max(0, i - 1));
  };

  const goNext = (e) => {
    e?.stopPropagation();
    setCurrentIndex((i) => Math.min(todayCelebrations.length - 1, i + 1));
  };

  return ReactDOM.createPortal(
    <>
      {person && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
            onClick={handleClose}
          />

          {/* Modal */}
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1051 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div
                className="modal-content text-center p-4 position-relative"
                style={{ zIndex: 1052 }}
              >
                {/* Confetti */}
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    pointerEvents: 'none',
                    zIndex: 1050
                  }}
                >
                  <Confetti
                    key={currentIndex}
                    width={window.innerWidth}
                    height={window.innerHeight}
                    recycle={false}
                    numberOfPieces={150}
                  />
                </div>

                <h4 className="mb-3 text-primary fw-bold text-center">{title}</h4>
                <hr className="mx-auto mb-4" style={{ borderTop: '3px solid #0d6efd' }} />

                <div className="celebration-dialog__stage">
                  {hasMultiple ? (
                    <button
                      type="button"
                      className="celebration-dialog__nav-btn"
                      aria-label="Previous celebration"
                      disabled={atStart}
                      onClick={goPrev}
                    >
                      <i className="icofont icofont-rounded-left" aria-hidden />
                    </button>
                  ) : null}

                  <div key={currentIndex} className="celebration-dialog__person">
                    <h5 className="mb-3 text-secondary fw-semibold">
                      {person.type === 'birthday' ? '🎂 Happy Birthday!' : '🎉 Work Anniversary!'}
                    </h5>

                    <CelebrationAvatar name={person.name} profilePicture={person.profilePicture} />

                    <p className="mb-1 fs-5 fw-bold text-dark">{person.name}</p>
                    <small className="text-muted">
                      {person.type === 'birthday'
                        ? `Wishing you a wonderful year ahead!`
                        : `Thank you for your dedication and hard work!`}
                    </small>
                  </div>

                  {hasMultiple ? (
                    <button
                      type="button"
                      className="celebration-dialog__nav-btn"
                      aria-label="Next celebration"
                      disabled={atEnd}
                      onClick={goNext}
                    >
                      <i className="icofont icofont-rounded-right" aria-hidden />
                    </button>
                  ) : null}
                </div>

                {hasMultiple ? (
                  <div className="celebration-dialog__pager">
                    <span className="celebration-dialog__counter" aria-live="polite">
                      {currentIndex + 1} / {todayCelebrations.length}
                    </span>
                    <div className="celebration-dialog__dots">
                      {todayCelebrations.map((item, i) => (
                        <button
                          key={`${item.type}-${item._id || item.id || item.name}-${i}`}
                          type="button"
                          className={`celebration-dialog__dot ${i === currentIndex ? 'is-active' : ''}`}
                          aria-label={`Go to celebration ${i + 1}`}
                          onClick={() => setCurrentIndex(i)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                <button type="button" className="btn btn-primary mt-3" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>,
    modalRoot
  );
};

export default CelebrationModal;
